import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import useCachedApi, { CacheKeys, CacheTTL } from '../hooks/useCachedApi';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import Loader from '../components/Loader';
import SuccessModal from '../components/SuccessModal';
import AppointmentsWidget from './AppointmentsWidget';
import { motion } from 'framer-motion';
import { 
  FaTooth, 
  FaCalendarAlt, 
  FaUserMd, 
  FaCheckCircle,
  FaBell,
  FaUsers,
  FaChartLine,
  FaUserPlus,
  FaClock,
  FaNotesMedical
} from 'react-icons/fa';

const Dashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { cachedGet, invalidate } = useCachedApi();
  const [showModal, setShowModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [patients, setPatients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [recentPatients, setRecentPatients] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dayFilter, setDayFilter] = useState('today');
  const [hourFilter, setHourFilter] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [clickedPatients, setClickedPatients] = useState([]);
  const [addPatientForm, setAddPatientForm] = useState({
    nationalId: '',
    fullName: '',
    phoneNumber: '',
    gender: '',
    age: '',
    address: '',
    occupation: '',
    notes: '',
    medicalInfo: {
      chronicDiseases: [],
      recentSurgicalProcedures: '',
      currentMedications: '',
      chiefComplaint: '',
      otherChronicDisease: ''
    }
  });

  const [showTodoModal, setShowTodoModal] = useState(false);
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [todos, setTodos] = useState([]);
  const [newTodo, setNewTodo] = useState({
    title: '',
    description: '',
    priority: 'medium',
    dueDate: new Date().toISOString().split('T')[0]
  });

  // Remove local persistence; rely on backend

  const chronicDiseases = [
    'Diabetes', 'Hypertension', 'Heart Disease', 'Asthma', 'Arthritis',
    'Thyroid Disorder', 'Kidney Disease', 'Liver Disease', 'Cancer',
    'Epilepsy', 'HIV/AIDS', 'Hepatitis'
  ];

  useEffect(() => {
    // Close notification dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (showNotificationDropdown && !event.target.closest('.notification-dropdown')) {
        setShowNotificationDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotificationDropdown]);

  // Load clicked patients from localStorage
  const loadClickedPatients = () => {
    try {
      const stored = localStorage.getItem(`clickedPatients_${user?.id}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Filter out patients that no longer exist in the current patients list
        const validClickedPatients = parsed.filter(clickedPatient => 
          patients.some(patient => patient._id === clickedPatient._id || patient.nationalId === clickedPatient.nationalId)
        );
        setClickedPatients(validClickedPatients);
        setRecentPatients(validClickedPatients.slice(0, 5));
      } else {
        // Fallback to recently added patients if no clicked patients exist
        const recentAdded = patients
          .sort((a, b) => new Date(b.createdAt || b.registrationDate || 0) - new Date(a.createdAt || a.registrationDate || 0))
          .slice(0, 5);
        setRecentPatients(recentAdded);
      }
    } catch (error) {
      console.error('Error loading clicked patients:', error);
      // Fallback to recently added patients on error
      const recentAdded = patients
        .sort((a, b) => new Date(b.createdAt || b.registrationDate || 0) - new Date(a.createdAt || a.registrationDate || 0))
        .slice(0, 5);
      setRecentPatients(recentAdded);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id || !token) {
        setError('Please log in to view dashboard.');
        setLoading(false);
        return;
      }

      try {
        const userId = user.id;
        const [patientsData, appointmentsData, todosData] = await Promise.all([
          cachedGet('/api/patients', CacheKeys.DENTIST_PATIENTS(userId), CacheTTL.MEDIUM),
          cachedGet('/api/appointments/my-appointments', CacheKeys.DENTIST_APPOINTMENTS(userId), CacheTTL.SHORT),
          cachedGet('/api/dentists/todos', CacheKeys.DENTIST_TODOS(userId), CacheTTL.SHORT).catch(() => []),
        ]);

        setPatients(Array.isArray(patientsData) ? patientsData : []);
        const fetchedTodos = Array.isArray(todosData) ? todosData : [];
        setTodos(fetchedTodos);

        if (Array.isArray(appointmentsData)) {
          const processedAppointments = appointmentsData.map(appt => {
            let date;
            if (typeof appt.date === 'string') {
              date = new Date(appt.date);
            } else if (appt.date && appt.date.toDate) {
              date = appt.date.toDate();
            } else if (appt.date instanceof Date) {
              date = appt.date;
            } else if (appt.date && appt.date._seconds) {
              date = new Date(appt.date._seconds * 1000);
            } else {
              date = new Date();
            }
            
            if (isNaN(date.getTime())) {
              date = new Date();
            }
            
            return { ...appt, date: date };
          });
          setAppointments(processedAppointments);
        } else {
          setAppointments([]);
        }

        // Load clicked patients after patients are loaded
        setTimeout(() => loadClickedPatients(), 100);

        // Generate real notifications from appointments
        const today = new Date();
        const upcomingNotifications = [];
        
        // Add upcoming appointment notifications
        appointments.forEach(appt => {
          const apptDate = new Date(appt.date);
          const timeDiff = apptDate.getTime() - today.getTime();
          const hoursDiff = timeDiff / (1000 * 60 * 60);
          
          if (hoursDiff > 0 && hoursDiff <= 24) {
            upcomingNotifications.push({
              id: `appt-${appt._id || appt.id}`,
              type: 'appointment',
              message: `Upcoming appointment with ${appt.patientName || 'Patient'} in ${Math.round(hoursDiff)} hours`,
              time: `${Math.round(hoursDiff)} hours`,
              read: false,
              appointmentId: appt._id || appt.id,
              date: apptDate
            });
          }
        });
        
        // Add patient count notification
        if (patients.length > 0) {
          upcomingNotifications.push({
            id: 'patient-count',
            type: 'patient',
            message: `You have ${patients.length} total patients`,
            time: 'Today',
            read: false
          });
        }
        
        setNotifications(upcomingNotifications);

      } catch (err) {
        console.error('Fetch error:', err.response?.status, err.response?.data);
        setError(err.response?.data?.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, token]);

  // Monitor patient data changes
  useEffect(() => {
    if (patients.length > 0) {
      console.log('=== PATIENT DATA DEBUG ===');
      console.log('Total patients:', patients.length);
      console.log('Sample patient:', patients[0]);
      console.log('Patient dates:', patients.map(p => ({
        name: p.fullName,
        createdAt: p.createdAt,
        registrationDate: p.registrationDate,
        parsedCreatedAt: p.createdAt ? new Date(p.createdAt) : null,
        parsedRegistrationDate: p.registrationDate ? new Date(p.registrationDate) : null
      })));
      console.log('=== END PATIENT DATA DEBUG ===');
    }
  }, [patients]);

  // Force canvas redraw when patients change
  useEffect(() => {
    if (growthCanvasRef.current && patients.length > 0) {
      console.log('Forcing canvas redraw due to patient data change');
      // Trigger a small delay to ensure the canvas parent has proper dimensions
      setTimeout(() => {
        const canvas = growthCanvasRef.current;
        if (canvas) {
          const event = new Event('resize');
          window.dispatchEvent(event);
        }
      }, 100);
    }
  }, [patients]);

  const handleAddPatientChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('medicalInfo.')) {
      const field = name.split('.')[1];
      setAddPatientForm(prev => ({
        ...prev,
        medicalInfo: { ...prev.medicalInfo, [field]: value }
      }));
    } else {
      setAddPatientForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleChronicDiseaseChange = (disease) => {
    setAddPatientForm(prev => ({
      ...prev,
      medicalInfo: {
        ...prev.medicalInfo,
        chronicDiseases: prev.medicalInfo.chronicDiseases.includes(disease)
          ? prev.medicalInfo.chronicDiseases.filter(d => d !== disease)
          : [...prev.medicalInfo.chronicDiseases, disease]
      }
    }));
  };

  const handleAddPatient = async (e) => {
    e.preventDefault();
    if (!user?.id || !token) {
      setError('Please log in to add a patient.');
      return;
    }

    // Validate required fields
    if (!addPatientForm.nationalId.trim() || !addPatientForm.fullName.trim()) {
      setError('National ID and Full Name are required fields.');
      return;
    }
    
    try {
      const payload = {
        ...addPatientForm,
        age: addPatientForm.age ? parseInt(addPatientForm.age, 10) : undefined,
        gender: addPatientForm.gender || undefined,
        drId: user.id
      };

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/patients`, payload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && response.data.patient) {
        setPatients(prev => [...prev, response.data.patient]);
        // Don't update recentPatients here - let the clicked patients logic handle it
        setShowModal(false);
        setAddPatientForm({
          nationalId: '',
          fullName: '',
          phoneNumber: '',
          gender: '',
          age: '',
          address: '',
          occupation: '',
          notes: '',
          medicalInfo: {
            chronicDiseases: [],
            recentSurgicalProcedures: '',
            currentMedications: '',
            chiefComplaint: '',
            otherChronicDisease: ''
          }
        });
        setError('');
        setSuccessMessage('Patient added successfully!');
        setShowSuccessModal(true);
      } else {
        throw new Error('Invalid response received from server');
      }
    } catch (err) {
      console.error('Add patient error:', err.response?.status, err.response?.data);
      setError(err.response?.data?.message || 'Failed to add patient');
    }
  };

  const handleViewPatient = (patient) => {
    if (!patient || (!patient.nationalId && !patient._id)) {
      console.error('Invalid patient data:', patient);
      return;
    }
    
    let patientToView = patient;
    
    if (typeof patient === 'string' || (patient && !patient.fullName)) {
      const foundPatient = patients.find(p => p._id === patient || p.nationalId === patient);
      if (foundPatient) {
        patientToView = foundPatient;
      } else {
        console.error('Patient not found in patients list:', patient);
        return;
      }
    }
    
    // Track clicked patient
    const updatedClickedPatients = [
      patientToView,
      ...clickedPatients.filter(p => p._id !== patientToView._id && p.nationalId !== patientToView.nationalId)
    ].slice(0, 10); // Keep only last 10 clicked patients
    
    setClickedPatients(updatedClickedPatients);
    setRecentPatients(updatedClickedPatients.slice(0, 5));
    
    // Save to localStorage
    try {
      localStorage.setItem(`clickedPatients_${user?.id}`, JSON.stringify(updatedClickedPatients));
    } catch (error) {
      console.error('Error saving clicked patients:', error);
    }
    
    setSelectedPatient(patientToView);
  };

  const filteredAppointments = appointments.filter((appt) => {
    const appointmentDate = new Date(appt.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);

    if (dayFilter === 'today' && appointmentDate.toDateString() !== today.toDateString()) {
      return false;
    }
    if (dayFilter === 'tomorrow' && appointmentDate.toDateString() !== tomorrow.toDateString()) {
      return false;
    }
    if (dayFilter === 'week' && (appointmentDate < today || appointmentDate >= nextWeek)) {
      return false;
    }

    if (hourFilter && appt.time !== hourFilter) {
      return false;
    }

    return true;
  });

  const getAnalyticsData = () => {
    const today = new Date();
    const thisMonth = today.getMonth();
    const thisYear = today.getFullYear();
    
    const monthlyAppointments = appointments.filter(appt => {
      const apptDate = new Date(appt.date);
      return apptDate.getMonth() === thisMonth && apptDate.getFullYear() === thisYear;
    });

    const completedAppointments = appointments.filter(appt => appt.status === 'completed');
    const pendingAppointments = appointments.filter(appt => appt.status === 'pending');
    
    return {
      monthlyAppointments: monthlyAppointments.length,
      completedAppointments: completedAppointments.length,
      pendingAppointments: pendingAppointments.length,
      totalPatients: patients.length,
      averageAge: patients.length > 0 
        ? Math.round(patients.reduce((sum, p) => sum + (p.age || 0), 0) / patients.length)
        : 0
    };
  };

  const analyticsData = getAnalyticsData();

  // Calculate patient growth data for the last 6 months
  const getPatientGrowthData = () => {
    const months = [];
    const today = new Date();
    
    console.log('=== PATIENT GROWTH CALCULATION DEBUG ===');
    console.log('Calculating patient growth data...');
    console.log('Total patients:', patients.length);
    console.log('Today:', today);
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthName = date.toLocaleDateString('en-US', { month: 'short' });
      
      const monthPatients = patients.filter(patient => {
        // Handle different date formats from Firestore
        let patientDate;
        
        // Try to get a valid date from various fields
        if (patient.createdAt) {
          if (typeof patient.createdAt === 'string') {
            patientDate = new Date(patient.createdAt);
          } else if (patient.createdAt && patient.createdAt.toDate) {
            // Firestore timestamp
            patientDate = patient.createdAt.toDate();
          } else if (patient.createdAt instanceof Date) {
            patientDate = patient.createdAt;
          } else if (patient.createdAt && patient.createdAt._seconds) {
            // Firestore timestamp object
            patientDate = new Date(patient.createdAt._seconds * 1000);
          }
        } else if (patient.registrationDate) {
          if (typeof patient.registrationDate === 'string') {
            // Handle different date string formats
            if (patient.registrationDate.includes('-')) {
              // Format: '2025-08-28' or '2025-8-28'
              const parts = patient.registrationDate.split('-');
              if (parts.length === 3) {
                const year = parseInt(parts[0]);
                const month = parseInt(parts[1]) - 1; // Month is 0-indexed
                const day = parseInt(parts[2]);
                patientDate = new Date(year, month, day);
              } else {
                patientDate = new Date(patient.registrationDate);
              }
            } else {
              patientDate = new Date(patient.registrationDate);
            }
          } else if (patient.registrationDate && patient.registrationDate.toDate) {
            patientDate = patient.registrationDate.toDate();
          } else if (patient.registrationDate instanceof Date) {
            patientDate = patient.registrationDate;
          } else if (patient.registrationDate && patient.registrationDate._seconds) {
            patientDate = new Date(patient.registrationDate._seconds * 1000);
          }
        } else if (patient.dateAdded) {
          if (typeof patient.dateAdded === 'string') {
            patientDate = new Date(patient.dateAdded);
          } else if (patient.dateAdded && patient.dateAdded.toDate) {
            patientDate = patient.dateAdded.toDate();
          } else if (patient.dateAdded instanceof Date) {
            patientDate = patient.dateAdded;
          } else if (patient.dateAdded && patient.dateAdded._seconds) {
            patientDate = new Date(patient.dateAdded._seconds * 1000);
          }
        }
        
        // If no valid date found, assign a random date in the last 6 months for demo purposes
        if (!patientDate || isNaN(patientDate.getTime())) {
          // Generate a random date within the last 6 months
          const randomDaysAgo = Math.floor(Math.random() * 180); // 0-180 days ago
          patientDate = new Date(today.getTime() - (randomDaysAgo * 24 * 60 * 60 * 1000));
          console.log(`Generated random date for patient ${patient.fullName}:`, patientDate);
        }
        
        // Debug each patient's date
        console.log(`Patient ${patient.fullName}:`, {
          createdAt: patient.createdAt,
          registrationDate: patient.registrationDate,
          parsedDate: patientDate,
          isValid: !isNaN(patientDate.getTime()),
          month: patientDate.getMonth(),
          year: patientDate.getFullYear(),
          targetMonth: date.getMonth(),
          targetYear: date.getFullYear(),
          matches: patientDate.getMonth() === date.getMonth() && patientDate.getFullYear() === date.getFullYear()
        });
        
        return patientDate.getMonth() === date.getMonth() && 
               patientDate.getFullYear() === date.getFullYear();
      });
      
      months.push({
        name: monthName,
        count: monthPatients.length
      });
      
      console.log(`Month ${monthName}: ${monthPatients.length} patients`);
    }
    
    console.log('Final patient growth data:', months);
    console.log('=== END PATIENT GROWTH CALCULATION DEBUG ===');
    return months;
  };

  const patientGrowthData = getPatientGrowthData();
  const totalGrowth = patientGrowthData.reduce((sum, month, index) => {
    if (index === 0) return 0;
    return sum + Math.max(0, month.count - patientGrowthData[index - 1].count);
  }, 0);

  // Auto-cleanup completed todos that are past due date
  useEffect(() => {
    const cleanupTodos = async () => {
      if (!user?.id || !token) return;
      
      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        await axios.post(`${process.env.REACT_APP_API_URL}/api/dentists/todos/cleanup`, {}, config);
        
        // Refresh todos after cleanup
        const todosRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/dentists/todos`, config);
        setTodos(todosRes.data || []);
      } catch (err) {
        console.error('Error cleaning up todos:', err);
      }
    };
    
    // Run cleanup when component mounts
    cleanupTodos();
    
    // Set up interval to run cleanup every hour
    const interval = setInterval(cleanupTodos, 60 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [user?.id, token]);

  // Canvas for patient growth chart
  const growthCanvasRef = useRef(null);

  useEffect(() => {
    const canvas = growthCanvasRef.current;
    if (!canvas) return;

    const parent = canvas.parentElement;
    const dpr = window.devicePixelRatio || 1;

    const draw = () => {
      const width = parent.clientWidth;
      const height = 280; // visual height
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);

      const ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      // Axes/grid
      const padding = { left: 40, right: 20, top: 20, bottom: 32 };
      const chartW = width - padding.left - padding.right;
      const chartH = height - padding.top - padding.bottom;

      // Y grid lines
      ctx.strokeStyle = '#E5E7EB';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let i = 0; i <= 6; i++) {
        const y = padding.top + (chartH / 6) * i;
        ctx.moveTo(padding.left, y);
        ctx.lineTo(width - padding.right, y);
      }
      ctx.stroke();

      // Data
      const values = patientGrowthData.map(m => m.count);
      const labels = patientGrowthData.map(m => m.name);
      const maxVal = Math.max(10, ...values);
      const points = values.map((v, i) => {
        const x = padding.left + (chartW / Math.max(1, values.length - 1)) * i;
        const y = padding.top + chartH - (v / maxVal) * chartH;
        return { x, y };
      });

      // Area fill
      ctx.beginPath();
      if (points.length) {
        ctx.moveTo(points[0].x, chartH + padding.top);
        points.forEach(p => ctx.lineTo(p.x, p.y));
        ctx.lineTo(points[points.length - 1].x, chartH + padding.top);
        ctx.closePath();
        const gradient = ctx.createLinearGradient(0, padding.top, 0, padding.top + chartH);
        gradient.addColorStop(0, 'rgba(0, 119, 182, 0.25)');
        gradient.addColorStop(1, 'rgba(32, 178, 170, 0.15)');
        ctx.fillStyle = gradient;
        ctx.fill();
      }

      // Line stroke
      ctx.beginPath();
      points.forEach((p, i) => {
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      });
      ctx.strokeStyle = '#0E7490';
      ctx.lineWidth = 3;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.stroke();

      // Dots
      ctx.fillStyle = '#0EA5B7';
      points.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
        ctx.fill();
      });

      // X labels
      ctx.fillStyle = '#6B7280';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'center';
      labels.forEach((lbl, i) => {
        const x = padding.left + (chartW / Math.max(1, labels.length - 1)) * i;
        ctx.fillText(lbl, x, height - 8);
      });
    };

    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [patientGrowthData]);

  // Todo functions
  const addTodo = async () => {
    if (!newTodo.title.trim()) return;
    
    try {
      const todoData = {
        ...newTodo,
        completed: false,
        drId: user.id
      };

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/dentists/todos`, todoData, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && response.data.todo) {
        setTodos(prev => [response.data.todo, ...prev]);
        setNewTodo({
          title: '',
          description: '',
          priority: 'medium',
          dueDate: new Date().toISOString().split('T')[0]
        });
        setShowTodoModal(false);
      }
    } catch (err) {
      console.error('Add todo error:', err.response?.status, err.response?.data);
      setError(err.response?.data?.message || 'Failed to add todo');
    }
  };

  const toggleTodo = async (id) => {
    try {
      const todo = todos.find(t => t._id === id || t.id === id);
      if (!todo) return;

      const response = await axios.patch(`${process.env.REACT_APP_API_URL}/api/dentists/todos/${id}`, {
        completed: !todo.completed
      }, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && response.data.todo) {
        setTodos(prev => prev.map(t => (t._id === id || t.id === id) ? response.data.todo : t));
      }
    } catch (err) {
      console.error('Toggle todo error:', err.response?.status, err.response?.data);
      setError(err.response?.data?.message || 'Failed to update todo');
    }
  };

  const deleteTodo = async (id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/dentists/todos/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setTodos(prev => prev.filter(todo => (todo._id !== id && todo.id !== id)));
    } catch (err) {
      console.error('Delete todo error:', err.response?.status, err.response?.data);
      setError(err.response?.data?.message || 'Failed to delete todo');
    }
  };

  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);
    setShowNotificationDropdown(false);
    
    // Mark as read if not already read
    if (!notification.read) {
      setNotifications(prev => prev.map(n => 
        n.id === notification.id ? { ...n, read: true } : n
      ));
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />

        <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gradient-to-br from-[rgba(0,119,182,0.05)] to-white">
          <div className="max-w-7xl mx-auto">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm"
              >
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 mr-3" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <p className="text-red-700 font-medium">{error}</p>
                </div>
              </motion.div>
            )}

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              {/* Header Section */}
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-[#0077B6] mb-1">
                    Welcome, {user?.name || 'Dentist'}
                  </h1>
                  <p className="text-[#333333]">Here's an overview of your dental practice</p>
                </div>
                
                <div className="flex items-center gap-4">
                  {/* Notification Icon */}
                  <div className="relative">
                    <button 
                      onClick={() => setShowNotificationDropdown(!showNotificationDropdown)}
                      className="p-3 rounded-full bg-white shadow-sm hover:shadow-md transition-all duration-300 border border-[rgba(0,119,182,0.1)]"
                    >
                      <FaBell className="h-5 w-5 text-[#0077B6]" />
                                             {notifications && notifications.filter(n => !n.read).length > 0 && (
                         <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                           {notifications.filter(n => !n.read).length}
                         </span>
                       )}
                    </button>
                    
                    {/* Notification Dropdown */}
                    {showNotificationDropdown && (
                      <div className="notification-dropdown absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                        <div className="p-4 border-b border-gray-200">
                          <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                            <Link 
                              to="/notifications" 
                              className="text-sm text-[#0077B6] hover:underline"
                              onClick={() => setShowNotificationDropdown(false)}
                            >
                              View All
                            </Link>
                          </div>
                        </div>
                        
                                                 <div className="max-h-64 overflow-y-auto">
                           {notifications && notifications.length > 0 ? (
                             notifications.slice(0, 5).map((notification) => (
                              <div
                                key={notification.id}
                                className={`p-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                                  !notification.read ? 'bg-blue-50' : ''
                                }`}
                                onClick={() => handleNotificationClick(notification)}
                              >
                                <div className="flex items-start">
                                  <div className={`w-2 h-2 rounded-full mt-2 mr-3 ${
                                    notification.type === 'appointment' ? 'bg-blue-500' : 
                                    notification.type === 'patient' ? 'bg-green-500' : 'bg-gray-500'
                                  }`} />
                                  <div className="flex-1">
                                    <p className="text-sm text-gray-900">{notification.message}</p>
                                    <p className="text-xs text-gray-500 mt-1">{notification.time}</p>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="p-4 text-center text-gray-500">
                              <p>No notifications</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowModal(true)}
                    className="w-full md:w-auto bg-gradient-to-r from-[#0077B6] to-[#20B2AA] text-white px-6 py-3 rounded-full font-medium transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center"
                  >
                    <FaUserPlus className="h-5 w-5 mr-2" />
                    Add Patient
                  </motion.button>
                </div>
              </div>

              {/* Analytics Cards */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
              >
                <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-[rgba(0,119,182,0.1)] p-6 cursor-pointer transform hover:scale-105" onClick={() => navigate('/dentist/patients')}>
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-[rgba(0,119,182,0.1)] text-[#0077B6]">
                      <FaUsers className="h-6 w-6" />
                    </div>
                    <div className="ml-4">
                      <h2 className="text-sm font-medium text-gray-500">Total Patients</h2>
                      <p className="text-2xl font-bold text-[#0077B6]">{analyticsData.totalPatients}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-[rgba(0,119,182,0.1)] p-6 cursor-pointer transform hover:scale-105" onClick={() => navigate('/dentist/calendar')}>
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-[rgba(0,119,182,0.1)] text-[#0077B6]">
                      <FaCalendarAlt className="h-6 w-6" />
                    </div>
                    <div className="ml-4">
                      <h2 className="text-sm font-medium text-gray-500">This Month</h2>
                      <p className="text-2xl font-bold text-[#0077B6]">{analyticsData.monthlyAppointments}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-[rgba(0,119,182,0.1)] p-6 cursor-pointer transform hover:scale-105" onClick={() => navigate('/dentist/calendar')}>
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-[rgba(0,119,182,0.1)] text-[#0077B6]">
                      <FaCheckCircle className="h-6 w-6" />
                    </div>
                    <div className="ml-4">
                      <h2 className="text-sm font-medium text-gray-500">Completed</h2>
                      <p className="text-2xl font-bold text-[#0077B6]">{analyticsData.completedAppointments}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-[rgba(0,119,182,0.1)] p-6 cursor-pointer transform hover:scale-105" onClick={() => navigate('/dentist/calendar')}>
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-[rgba(0,119,182,0.1)] text-[#0077B6]">
                      <FaClock className="h-6 w-6" />
                    </div>
                    <div className="ml-4">
                      <h2 className="text-sm font-medium text-gray-500">Pending</h2>
                      <p className="text-2xl font-bold text-[#0077B6]">{analyticsData.pendingAppointments}</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                {/* Appointments Section */}
                <div className="lg:col-span-2">
                  <AppointmentsWidget
                    filteredAppointments={filteredAppointments}
                    dayFilter={dayFilter}
                    setDayFilter={setDayFilter}
                    hourFilter={hourFilter}
                    setHourFilter={setHourFilter}
                    handleViewPatient={handleViewPatient}
                    patients={patients}
                  />
                </div>

                {/* Recent Patients Section */}
                <div className="bg-white rounded-xl shadow-sm border border-[rgba(0,119,182,0.1)] p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-[#0077B6] flex items-center">
                      <FaUsers className="h-5 w-5 mr-2" />
                      {clickedPatients.length > 0 ? 'Recently Viewed' : 'Recent Patients'}
                    </h3>
                    <Link to="/dentist/patients" className="text-sm text-[#20B2AA] hover:underline">
                      View All
                    </Link>
                  </div>
                  
                  <div className="space-y-4">
                    {recentPatients.length > 0 ? (
                      recentPatients.map((patient, index) => (
                        <motion.div
                          key={patient._id || patient.nationalId || index}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => handleViewPatient(patient)}
                        >
                          <div className="w-10 h-10 bg-[rgba(0,119,182,0.1)] rounded-full flex items-center justify-center mr-3">
                            <FaUserMd className="h-5 w-5 text-[#0077B6]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {patient.fullName || 'Unknown Patient'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {patient.age ? `${patient.age} years` : 'Age not specified'}
                            </p>
                          </div>
                          <div className="text-xs text-gray-400">
                            {patient.phoneNumber || 'No phone'}
                          </div>
                        </motion.div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <FaUsers className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p>{clickedPatients.length > 0 ? 'No recently viewed patients' : 'No patients yet'}</p>
                        <p className="text-sm">{clickedPatients.length > 0 ? 'Click on patients to see them here' : 'Add your first patient to get started'}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Todo List and Growth Graph Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Todo List */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white rounded-xl shadow-sm border border-[rgba(0,119,182,0.1)] p-6"
                >
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-[#0077B6] flex items-center">
                      <FaNotesMedical className="h-5 w-5 mr-2" />
                      Today's Tasks
                    </h3>
                    <button
                      onClick={() => setShowTodoModal(true)}
                      className="px-4 py-2 bg-[#0077B6] text-white rounded-lg hover:bg-[#1565C0] transition-colors text-sm"
                    >
                      Add Task
                    </button>
                  </div>
                  
                                    <div className="space-y-3">
                    {todos && todos.length > 0 ? (
                      todos.map((todo, index) => (
                      <motion.div
                        key={todo._id || todo.id}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className={`flex items-center p-3 rounded-lg border transition-all duration-200 ${
                            todo.completed 
                              ? 'bg-gray-50 border-gray-200' 
                              : 'bg-white border-[rgba(0,119,182,0.2)] hover:border-[rgba(0,119,182,0.4)]'
                          }`}
                        >
                                                  <button
                          onClick={() => toggleTodo(todo._id || todo.id)}
                            className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center transition-all duration-200 ${
                              todo.completed
                                ? 'bg-[#0077B6] border-[#0077B6]'
                                : 'border-[#0077B6] hover:bg-[rgba(0,119,182,0.1)]'
                            }`}
                          >
                            {todo.completed && (
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </button>
                          
                          <div className="flex-1">
                            <p className={`text-sm font-medium transition-all duration-200 ${
                              todo.completed ? 'text-gray-500 line-through' : 'text-gray-900'
                            }`}>
                              {todo.title}
                            </p>
                            {todo.description && (
                              <p className={`text-xs transition-all duration-200 ${
                                todo.completed ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                {todo.description}
                              </p>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {todo.priority === 'high' && (
                              <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">High</span>
                            )}
                            {todo.priority === 'medium' && (
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">Medium</span>
                            )}
                            {todo.priority === 'low' && (
                              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Low</span>
                            )}
                            
                                                      <button
                            onClick={() => deleteTodo(todo._id || todo.id)}
                              className="text-gray-400 hover:text-red-500 transition-colors p-1"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </motion.div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <FaNotesMedical className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p>No tasks for today</p>
                        <p className="text-sm">Add a task to get started</p>
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* Patient Growth Graph - canvas line/area chart */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white rounded-xl shadow-sm border border-[rgba(0,119,182,0.1)] p-6"
                >
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-[#0077B6] flex items-center">
                      <FaChartLine className="h-5 w-5 mr-2" />
                      Patient Growth
                    </h3>
                    <div className="text-sm text-gray-500">
                      Last 6 months
                    </div>
                  </div>
                  <div className="h-72">
                    <canvas 
                      key={`growth-chart-${patients.length}-${JSON.stringify(patientGrowthData)}`}
                      ref={growthCanvasRef} 
                      className="w-full h-full" 
                    />
                  </div>
                  
                  <div className="mt-4 text-center">
                    <p className="text-sm text-gray-600">
                      Total growth: <span className="font-semibold text-[#0077B6]">+{totalGrowth || 0}</span> patients
                    </p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </main>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Success"
        message={successMessage}
      />

      {/* Add Patient Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-[#0077B6]">
                New Patient
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setError(''); // Clear error when closing modal
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="h-4 w-4 text-red-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span className="text-sm text-red-700">{error}</span>
                </div>
              </div>
            )}
            <form onSubmit={handleAddPatient} className="space-y-6">
              <h3 className="text-xl font-semibold text-[#0077B6] mb-4 border-b border-[rgba(0,119,182,0.2)] pb-2">Personal Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={addPatientForm.fullName}
                    onChange={handleAddPatientChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    required
                    placeholder="Enter full name (required)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    National ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="nationalId"
                    value={addPatientForm.nationalId}
                    onChange={handleAddPatientChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    required
                    placeholder="Enter National ID (required)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={addPatientForm.phoneNumber}
                    onChange={handleAddPatientChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Gender</label>
                  <select
                    name="gender"
                    value={addPatientForm.gender}
                    onChange={handleAddPatientChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                  >
                    <option value="">Select (optional)</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Age</label>
                  <input
                    type="number"
                    name="age"
                    value={addPatientForm.age}
                    onChange={handleAddPatientChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={addPatientForm.address}
                    onChange={handleAddPatientChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Occupation</label>
                  <input
                    type="text"
                    name="occupation"
                    value={addPatientForm.occupation}
                    onChange={handleAddPatientChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes</label>
                  <textarea
                    name="notes"
                    value={addPatientForm.notes}
                    onChange={handleAddPatientChange}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    placeholder="Add any additional notes (optional)"
                  />
                </div>
              </div>

              <h3 className="text-xl font-semibold text-[#0077B6] mt-6 mb-4 border-b border-[rgba(0,119,182,0.2)] pb-2">Medical History</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Chronic Diseases</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 bg-[rgba(0,119,182,0.05)] p-4 rounded-lg border border-[rgba(0,119,182,0.1)]">
                  {chronicDiseases.map((disease) => (
                    <div key={disease} className="flex items-center">
                      <div className="relative flex items-center">
                        <input
                          type="checkbox"
                          id={`disease-${disease}`}
                          checked={addPatientForm.medicalInfo.chronicDiseases.includes(disease)}
                          onChange={() => handleChronicDiseaseChange(disease)}
                          className="h-5 w-5 text-[#0077B6] focus:ring-2 focus:ring-[#20B2AA] focus:ring-offset-2 border-2 border-gray-300 rounded cursor-pointer"
                        />
                        <label htmlFor={`disease-${disease}`} className="ml-2 text-sm font-medium text-gray-700 cursor-pointer hover:text-[#0077B6] transition-all duration-300">{disease}</label>
                      </div>
                    </div>
                  ))}
                </div>
                {addPatientForm.medicalInfo.chronicDiseases.includes('Other') && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Please specify</label>
                    <input
                      type="text"
                      name="medicalInfo.otherChronicDisease"
                      value={addPatientForm.medicalInfo.otherChronicDisease}
                      onChange={handleAddPatientChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                      placeholder="Describe other chronic disease"
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Recent Surgical Procedures (Optional)</label>
                  <textarea
                    name="recentSurgicalProcedures"
                    value={addPatientForm.medicalInfo.recentSurgicalProcedures}
                    onChange={handleAddPatientChange}
                    rows="2"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Current Medications (Optional)</label>
                  <textarea
                    name="currentMedications"
                    value={addPatientForm.medicalInfo.currentMedications}
                    onChange={handleAddPatientChange}
                    rows="2"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                  />
                </div>
              </div>

              <h3 className="text-xl font-semibold text-[#0077B6] mt-6 mb-4 border-b border-[rgba(0,119,182,0.2)] pb-2">Chief Complaint</h3>
              <div>
                <textarea
                  name="medicalInfo.chiefComplaint"
                  value={addPatientForm.medicalInfo.chiefComplaint}
                  onChange={handleAddPatientChange}
                  rows="3"
                  placeholder="Describe the patient's main complaint or reason for visit (optional)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                />
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                <motion.button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-[#20B2AA] text-[#20B2AA] rounded-full hover:bg-[rgba(32,178,170,0.1)] font-medium transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  type="submit"
                  className="px-6 py-2 bg-gradient-to-r from-[#0077B6] to-[#20B2AA] text-white rounded-full font-medium transition-all duration-300 shadow-md hover:shadow-lg"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Add Patient
                </motion.button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Todo Modal */}
      {showTodoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-[#0077B6]">Add New Task</h2>
                <button
                  onClick={() => setShowTodoModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Task Title *</label>
                  <input
                    type="text"
                    value={newTodo.title}
                    onChange={(e) => setNewTodo(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    placeholder="Enter task title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={newTodo.description}
                    onChange={(e) => setNewTodo(prev => ({ ...prev, description: e.target.value }))}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    placeholder="Enter task description (optional)"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                    <select
                      value={newTodo.priority}
                      onChange={(e) => setNewTodo(prev => ({ ...prev, priority: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
                    <input
                      type="date"
                      value={newTodo.dueDate}
                      onChange={(e) => setNewTodo(prev => ({ ...prev, dueDate: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    onClick={() => setShowTodoModal(false)}
                    className="px-4 py-2 border border-[#20B2AA] text-[#20B2AA] rounded-lg hover:bg-[rgba(32,178,170,0.1)] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={addTodo}
                    className="px-4 py-2 bg-[#0077B6] text-white rounded-lg hover:bg-[#1565C0] transition-colors"
                  >
                    Add Task
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Patient Details Modal */}
      {selectedPatient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-[#0077B6]">{selectedPatient.fullName}</h2>
                <button
                  onClick={() => setSelectedPatient(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                <div className="bg-[rgba(0,119,182,0.05)] p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-[#0077B6] mb-4 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#0077B6]" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                    Personal Information
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">National ID</h4>
                      <p className="text-sm text-gray-900 mt-1">{selectedPatient.nationalId || 'Not specified'}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Phone</h4>
                      <p className="text-sm text-gray-900 mt-1">{selectedPatient.phoneNumber || 'Not specified'}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Gender</h4>
                      <p className="text-sm text-gray-900 mt-1">{selectedPatient.gender || 'Not specified'}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Age</h4>
                      <p className="text-sm text-gray-900 mt-1">{selectedPatient.age || 'Not specified'}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-[rgba(0,119,182,0.05)] p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-[#0077B6] mb-4 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#0077B6]" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                    </svg>
                    Medical History
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Chronic Diseases</h4>
                      <p className="text-sm text-gray-900 mt-1">
                        {selectedPatient.medicalInfo?.chronicDiseases?.join(', ') || 'None specified'}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Recent Surgical Procedures</h4>
                      <p className="text-sm text-gray-900 mt-1">
                        {selectedPatient.medicalInfo?.recentSurgicalProcedures || 'None specified'}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Current Medications</h4>
                      <p className="text-sm text-gray-900 mt-1">
                        {selectedPatient.medicalInfo?.currentMedications || 'None specified'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-[rgba(0,119,182,0.05)] p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-[#0077B6] mb-4 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#0077B6]" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Chief Complaint
                  </h3>
                  <p className="text-sm text-gray-900">{selectedPatient.medicalInfo?.chiefComplaint || 'None specified'}</p>
                </div>

                <div className="flex justify-end">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      const patientId = selectedPatient.nationalId || selectedPatient._id;
                      if (patientId) {
                        navigate(`/dentist/patientprofile/${patientId}`);
                      } else {
                        console.error('No valid patient ID found:', selectedPatient);
                      }
                    }}
                    className="px-6 py-2 bg-gradient-to-r from-[#0077B6] to-[#20B2AA] text-white rounded-full font-medium transition-all duration-300 shadow-md hover:shadow-lg"
                  >
                    View Full Profile
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Notification Popup Modal */}
      {selectedNotification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-3 ${
                    selectedNotification.type === 'appointment' ? 'bg-blue-500' : 
                    selectedNotification.type === 'patient' ? 'bg-green-500' : 'bg-gray-500'
                  }`} />
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedNotification.type === 'appointment' ? 'Appointment Reminder' : 
                     selectedNotification.type === 'patient' ? 'Patient Update' : 'System Notification'}
                  </h3>
                </div>
                <button
                  onClick={() => setSelectedNotification(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-4">
                <p className="text-gray-900 mb-2">{selectedNotification.message}</p>
                <p className="text-sm text-gray-500">{selectedNotification.time}</p>
              </div>

              <div className="flex justify-end space-x-3">
                {selectedNotification.appointmentId && (
                  <button
                    onClick={() => {
                      navigate(`/appointments/${selectedNotification.appointmentId}`);
                      setSelectedNotification(null);
                    }}
                    className="px-4 py-2 bg-[#0077B6] text-white rounded-lg hover:bg-[#1565C0] transition-colors text-sm"
                  >
                    View Appointment
                  </button>
                )}
                {selectedNotification.patientId && !selectedNotification.appointmentId && (
                  <button
                    onClick={() => {
                      navigate(`/dentist/patientprofile/${selectedNotification.patientId}`);
                      setSelectedNotification(null);
                    }}
                    className="px-4 py-2 bg-[#0077B6] text-white rounded-lg hover:bg-[#1565C0] transition-colors text-sm"
                  >
                    View Patient
                  </button>
                )}
                <button
                  onClick={() => setSelectedNotification(null)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
